import { AddOutlined, Delete, DragIndicator } from '@mui/icons-material';
import { Box, IconButton } from '@mui/material';
import { styled } from '@mui/material';
import DraggableList from 'components/DraggableList';
import { Row } from 'further-ui/components/Layout';
import NumberInput from 'further-ui/components/NumberInput';
import { carrySettings } from 'further-ui/validations';
import React from 'react';
import { Control, Controller } from 'react-hook-form';
import { z } from 'zod';
import CarriedInterestRateRowLayout from './CarriedInterestRateRowLayout';

type FormValues = z.infer<
  typeof carrySettings.CarriedInterestFeeTemplatesSettings
>;

const Container = styled(Box)(({ theme }) => ({
  width: '100%',
  border: `1px solid ${theme.palette.divider}`,
  marginTop: -1,
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
}));

type Props = {
  templateIndex: number;
  index: number;
  control: Control<FormValues>;
  onAddRate: () => void;
  onRemoveRate: (index: number) => void;
  showThreshold?: boolean;
  allowDelete: boolean;
  draggable?: boolean;
};

const CarriedInterestRate: React.FC<Props> = ({
  templateIndex,
  index,
  control,
  onAddRate,
  onRemoveRate,
  showThreshold,
  allowDelete,
  draggable = true,
}) => {
  return (
    <Container>
      <CarriedInterestRateRowLayout
        rate={
          <Controller
            name={`carriedInterestFeeTemplates.${templateIndex}.carriedInterestRates.${index}.rate`}
            control={control}
            render={({
              fieldState: { error },
              field: { onChange, ...rest },
            }) => (
              <NumberInput
                {...rest}
                decimals={10}
                onChange={onChange}
                size="small"
                placeholder="Enter rate"
                fullWidth
                min={0}
                max={100}
                error={!!error}
                helperText={error?.message}
                showPercentSuffix
              />
            )}
          />
        }
        dpiThreshold={
          showThreshold ? (
            <Controller
              name={`carriedInterestFeeTemplates.${templateIndex}.carriedInterestRates.${index}.dpiThreshold`}
              control={control}
              render={({
                fieldState: { error },
                field: { onChange, value, ...rest },
              }) => (
                <NumberInput
                  {...rest}
                  fullWidth
                  suffix="x"
                  size="small"
                  placeholder="Enter threshold"
                  value={value ?? undefined}
                  decimals={10}
                  onChange={onChange}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          ) : null
        }
        actions={
          <Row centered>
            <IconButton size="small" color="primary" onClick={onAddRate}>
              <AddOutlined fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => onRemoveRate(index)}
              disabled={!allowDelete}
            >
              <Delete fontSize="small" />
            </IconButton>
            {draggable ? (
              <DraggableList.Handle isFlexItem>
                <DragIndicator fontSize="small" />
              </DraggableList.Handle>
            ) : null}
          </Row>
        }
      />
    </Container>
  );
};

export default CarriedInterestRate;
